import { FC, useState } from 'react'
import { IRangeSliderProps } from './types'
import sharedStyles from '@muiscss/sharedStyles.module.scss'
import styles from './styles.module.scss'
import clsx from 'clsx'

export const RangeSlider: FC<IRangeSliderProps> = ({
  disabled = false,
  id,
  label,
  max,
  min,
  name,
  onBlur,
  onChange,
  onFocus,
  scale,
  size = 'md',
  step = 1,
  value,
  className,
  hasTooltips = false,
  hasMarkers = false,
  hasSubMarkers = false,
  tooltipPrefix,
  tooltipSuffix,
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState(value)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(event.currentTarget.value)
    event.currentTarget.id === id && setCurrentValue(val)
    onChange && onChange(event)
  }

  return (
    <div
      className={clsx(
        styles.wrap,
        {
          [sharedStyles.disabled]: disabled,
        },
        className,
      )}
    >
      {label && (
        <label className={clsx(sharedStyles.label)} data-testid="mui-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div className={styles.position}>
        <input
          className={clsx(styles.slider, styles[size])}
          data-testid="mui-range-slider"
          value={currentValue}
          disabled={disabled}
          id={id}
          name={name}
          aria-label={`${name} Range Slider`}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => onBlur?.(event)}
          onChange={handleChange}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => onFocus?.(event)}
          step={step}
          type="range"
          min={min}
          max={max}
          {...props}
        />
        <div
          className={clsx(styles.track, styles[size])}
          role="presentation"
          style={{
            left: '0',
            width: `${(currentValue / max) * 100}%`,
          }}
        >
          <div className={clsx(styles.thumb, styles[size])}>
            {hasTooltips && (
              <div className={clsx(styles.tooltip, styles[size])}>
                {tooltipPrefix && tooltipPrefix}
                {currentValue}
                {tooltipSuffix && tooltipSuffix}
              </div>
            )}
          </div>
        </div>
      </div>
      {scale && (
        <datalist className={clsx(styles.scale, styles[size])} id={`${id}_scale`}>
          {scale.flatMap((item: string, index: number, arr: string[]) => {
            const isLastItem = index === arr.length - 1
            const halfwayIndex = index + 0.5
            const halfwayItem = isLastItem ? undefined : `${item}/${arr[index + 1]}`
            const currentPercent = (100 * index) / (arr.length - 1)
            const nextPercent = isLastItem ? undefined : (100 * halfwayIndex) / (arr.length - 1)

            return [
              <option
                key={item.replace(/\s/g, '')}
                value={item}
                style={{ left: `${currentPercent}%` }}
                className={clsx(styles.marker, { [styles.hasMarkers]: hasMarkers })}
              >
                {item}
              </option>,
              halfwayItem && hasSubMarkers && (
                <option
                  key={`halfway-${item.replace(/\s/g, '')}`}
                  value={halfwayItem}
                  style={{ left: `${nextPercent}%` }}
                  disabled={true}
                  className={clsx(styles.marker, styles.halfwayMarker, {
                    [styles.hasMarkers]: hasMarkers,
                  })}
                >
                  -
                </option>
              ),
            ]
          })}
        </datalist>
      )}
    </div>
  )
}
